import { useSidebarState } from '@src/hooks/useSidebarState';
import { useUpdateAvailability } from '@src/hooks/useUpdateAvailability';
import DownloadArrowIcon from '@src/svgs/DownloadArrowIcon';
import GiftIcon from '@src/svgs/GiftIcon';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import styled from 'styled-components';

import { SidebarTooltip } from '../SidebarTooltip';
import { SIDEBAR_SPRING } from './sidebarMotion';

// Fade the text in/out on collapse — the card's `overflow: hidden` clips it
// cleanly while the sidebar width animates, so no width-collapse needed.
const TEXT_FADE = {
  hidden: { opacity: 0 },
  shown: { opacity: 1 },
};

interface SidebarUpdateCardProps {
  collapsed: boolean;
}

/**
 * Passive "Update Available" card pinned near the bottom of the sidebar. Click
 * saves any unsaved work and reloads to the latest deployment. Replaces the old
 * blocking RefreshNotification popup so an available update no longer interrupts
 * editing. Collapses to an icon-with-dot + tooltip when the sidebar is collapsed.
 */
const SidebarUpdateCard: React.FC<SidebarUpdateCardProps> = ({ collapsed }) => {
  const { isUpdateAvailable, isUpdating, onUpdate } = useUpdateAvailability();
  const { showLabels } = useSidebarState();

  if (!isUpdateAvailable) return null;

  const label = isUpdating ? 'Updating…' : 'Update Available';

  return (
    <Card
      $collapsed={collapsed}
      onClick={onUpdate}
      disabled={isUpdating}
      aria-busy={isUpdating}
      data-testid="sidebar-update-card"
    >
      <IconWrapper>
        {isUpdating ? (
          <Spinner data-testid="sidebar-update-spinner" />
        ) : (
          <>
            <GiftIcon fillColor="var(--primary)" strokeColor="#000" />
            <NotificationDot data-testid="sidebar-update-dot" />
          </>
        )}
      </IconWrapper>
      <AnimatePresence initial={false}>
        {showLabels && (
          <TextBlock
            key="update-text"
            variants={TEXT_FADE}
            initial="hidden"
            animate="shown"
            exit="hidden"
            transition={SIDEBAR_SPRING}
          >
            <Title>{label}</Title>
          </TextBlock>
        )}
      </AnimatePresence>
      {showLabels && !isUpdating && (
        <EndIcon>
          <DownloadArrowIcon strokeColor="var(--primary)" />
        </EndIcon>
      )}
      {!showLabels && <Tooltip>{label}</Tooltip>}
    </Card>
  );
};

export default SidebarUpdateCard;

const Tooltip = styled(SidebarTooltip)``;

const Card = styled.button<{ $collapsed: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: calc(100% - 20px);
  box-sizing: border-box;
  margin: 0 10px 12px;
  padding: ${p => (p.$collapsed ? '0 10px' : '10px 10px')};
  min-height: ${p => (p.$collapsed ? '30px' : 'auto')};
  border-radius: 8px;
  border: 1px solid var(--primary);
  background: var(--sidebar-hover);
  color: var(--text-secondary);
  cursor: pointer;
  text-align: left;
  /* overflow stays visible so the collapsed-state tooltip can render outside
   * the card's right edge (matches WhatsNewButton). Text clipping during the
   * width animation is handled on TextBlock instead. */
  transition:
    background var(--transition-fast) ease,
    filter var(--transition-fast) ease;

  &:hover {
    background: var(--sidebar-active);
    filter: brightness(1.08);
  }

  &:hover ${Tooltip} {
    opacity: 1;
  }

  &:disabled {
    cursor: default;
    filter: none;
  }
`;

const EndIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const IconWrapper = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const Spinner = styled.span`
  width: 16px;
  height: 16px;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: sidebar-update-spin 0.7s linear infinite;

  @keyframes sidebar-update-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const NotificationDot = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--primary);
  border: 1.5px solid var(--sidebar);
  box-shadow: var(--glow-primary);
`;

const TextBlock = styled(motion.span)`
  display: flex;
  /* grow to push the arrow to the right edge, but never shrink below the label
   * width — shrinking is what was clipping "Update Available". */
  flex: 1 0 auto;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
`;

const Title = styled.span`
  color: var(--foreground);
  font-size: 13px;
  font-weight: var(--font-weight-semi-bold);
  line-height: 1.2;
  white-space: nowrap;
`;
